import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DomainEntityType, SavedViewInput, ViewLayout } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { storage } from '@/lib/utils';
import type { FilterValues } from '@/components/data/filters';

/** A saved view as returned by `GET /views` (system views are seeded, users add their own). */
export interface SavedView {
  id: string;
  entityType: DomainEntityType;
  name: string;
  layout: ViewLayout;
  filters: Record<string, unknown>;
  sorts: Array<{ id: string; desc: boolean }>;
  columns: string[];
  groupBy?: string | null;
  isDefault?: boolean;
  isSystem?: boolean;
  shared?: boolean;
  ownerId?: string | null;
}

export interface ViewState {
  layout: ViewLayout;
  filters: FilterValues;
  sorts: Array<{ id: string; desc: boolean }>;
  columns: string[];
  groupBy: string | null;
}

export interface UseSavedViewsOptions {
  /** State when no view is selected (e.g. `{ layout: 'TABLE', filters: { openOnly: true } }`). */
  defaults?: Partial<ViewState>;
}

export interface UseSavedViewsResult {
  views: SavedView[];
  isLoading: boolean;
  activeViewId: string | null;
  activeView: SavedView | null;
  selectView: (id: string | null) => void;
  /** Current, possibly modified, state (what the page should render with). */
  state: ViewState;
  setLayout: (layout: ViewLayout) => void;
  setFilters: (filters: FilterValues) => void;
  setSorts: (sorts: ViewState['sorts']) => void;
  setColumns: (columns: string[]) => void;
  setGroupBy: (groupBy: string | null) => void;
  /** True when the state differs from the selected view (or from the defaults). */
  isDirty: boolean;
  /** Discard local changes and go back to the selected view. */
  reset: () => void;
  saveView: (input: { name: string; shared?: boolean; isDefault?: boolean }) => Promise<SavedView>;
  updateView: (id: string, patch: Partial<SavedViewInput>) => Promise<SavedView>;
  deleteView: (id: string) => Promise<void>;
  isSaving: boolean;
}

const EMPTY_STATE: ViewState = { layout: 'TABLE', filters: {}, sorts: [], columns: [], groupBy: null };

function stateFromView(view: SavedView): ViewState {
  return {
    layout: view.layout ?? 'TABLE',
    filters: (view.filters ?? {}) as FilterValues,
    sorts: view.sorts ?? [],
    columns: view.columns ?? [],
    groupBy: view.groupBy ?? null,
  };
}

function sameState(a: ViewState, b: ViewState): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

function unwrapList<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
    return (data as { items: T[] }).items;
  }
  return [];
}

/**
 * Deep-link filters from the URL (`/submissions?needsChase=true&waitingOn=US`): `true`/`false`
 * become booleans, a repeated key becomes an array, anything else stays a string. Returns null
 * when the URL carries no filter.
 */
export function filtersFromSearch(search: string): FilterValues | null {
  const params = new URLSearchParams(search);
  const out: FilterValues = {};
  for (const key of new Set(params.keys())) {
    const values = params.getAll(key).filter((v) => v !== '');
    if (values.length === 0) continue;
    if (values.length > 1) out[key] = values;
    else if (values[0] === 'true') out[key] = true;
    else if (values[0] === 'false') out[key] = false;
    else out[key] = values[0];
  }
  return Object.keys(out).length > 0 ? out : null;
}

function withUrlFilters(state: ViewState, urlFilters: FilterValues | null): ViewState {
  return urlFilters ? { ...state, filters: { ...state.filters, ...urlFilters } } : state;
}

/**
 * Saved views for an entity list (Notion-parity). Loads `GET /views?entityType=`, remembers the
 * chosen view per entity in localStorage and exposes the working state (layout, filters, sorts,
 * columns, groupBy) plus save/update/delete mutations. Query parameters in the URL on mount
 * (dashboard deep links) are merged into the filters as unsaved changes and then removed.
 *
 *   const sv = useSavedViews('POSITION', { defaults: { filters: { openOnly: true } } });
 *   <ViewSwitcher {...sv} filterDefs={FILTERS} />
 *   const query = useQuery({ queryKey: queryKeys.list('positions', sv.state.filters), ... });
 */
export function useSavedViews(
  entityType: DomainEntityType,
  options: UseSavedViewsOptions = {},
): UseSavedViewsResult {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const storageKey = `clawhire.view.${entityType}`;
  const defaults = React.useMemo<ViewState>(
    () => ({ ...EMPTY_STATE, ...options.defaults }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaults are static per page
    [entityType],
  );

  const viewsQuery = useQuery({
    queryKey: queryKeys.views(entityType),
    queryFn: async () => unwrapList<SavedView>(await api.get<unknown>('/views', { query: { entityType } })),
    staleTime: 60_000,
  });
  const views = React.useMemo<SavedView[]>(() => viewsQuery.data ?? [], [viewsQuery.data]);

  const [urlFilters] = React.useState<FilterValues | null>(() => filtersFromSearch(location.search));
  const pendingUrlFilters = React.useRef<FilterValues | null>(urlFilters);
  const [activeViewId, setActiveViewId] = React.useState<string | null>(() => storage.get(storageKey));
  const [state, setState] = React.useState<ViewState>(() => withUrlFilters(defaults, urlFilters));
  const appliedViewRef = React.useRef<string | null | undefined>(undefined);

  // Deep-link params are consumed once; drop them so a reload or a view switch starts clean.
  React.useEffect(() => {
    if (!urlFilters) return;
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, for the URL the page was opened with
  }, []);

  // Resolve the active view once the list is loaded: stored id, else the default view, else none.
  React.useEffect(() => {
    if (!viewsQuery.isSuccess) return;
    let id = activeViewId;
    if (id && !views.some((v) => v.id === id)) id = null;
    if (!id) id = views.find((v) => v.isDefault)?.id ?? null;
    if (id !== activeViewId) setActiveViewId(id);
    if (appliedViewRef.current !== id) {
      appliedViewRef.current = id;
      const view = id ? views.find((v) => v.id === id) : undefined;
      setState(withUrlFilters(view ? stateFromView(view) : defaults, pendingUrlFilters.current));
      pendingUrlFilters.current = null;
    }
  }, [viewsQuery.isSuccess, views, activeViewId, defaults]);

  const selectView = React.useCallback(
    (id: string | null) => {
      setActiveViewId(id);
      if (id) storage.set(storageKey, id);
      else storage.remove(storageKey);
      appliedViewRef.current = id;
      const view = id ? views.find((v) => v.id === id) : undefined;
      setState(view ? stateFromView(view) : defaults);
    },
    [views, defaults, storageKey],
  );

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const baseline = activeView ? stateFromView(activeView) : defaults;
  const isDirty = !sameState(state, baseline);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.views(entityType) });

  const saveMutation = useMutation({
    mutationFn: (input: { name: string; shared?: boolean; isDefault?: boolean }) =>
      api.post<SavedView>('/views', {
        entityType,
        name: input.name,
        shared: input.shared,
        isDefault: input.isDefault,
        layout: state.layout,
        filters: state.filters,
        sorts: state.sorts,
        columns: state.columns,
        groupBy: state.groupBy ?? undefined,
      } satisfies SavedViewInput),
    onSuccess: async (view) => {
      await invalidate();
      queryClient.setQueryData<SavedView[]>(queryKeys.views(entityType), (old) =>
        old && !old.some((v) => v.id === view.id) ? [...old, view] : old,
      );
      appliedViewRef.current = view.id;
      setActiveViewId(view.id);
      storage.set(storageKey, view.id);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<SavedViewInput> }) =>
      api.patch<SavedView>(`/views/${id}`, patch),
    onSuccess: () => invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/views/${id}`),
    onSuccess: async (_res, id) => {
      await invalidate();
      if (id === activeViewId) selectView(null);
    },
  });

  return {
    views,
    isLoading: viewsQuery.isLoading,
    activeViewId,
    activeView,
    selectView,
    state,
    setLayout: (layout) => setState((s) => ({ ...s, layout })),
    setFilters: (filters) => setState((s) => ({ ...s, filters })),
    setSorts: (sorts) => setState((s) => ({ ...s, sorts })),
    setColumns: (columns) => setState((s) => ({ ...s, columns })),
    setGroupBy: (groupBy) => setState((s) => ({ ...s, groupBy })),
    isDirty,
    reset: () => setState(baseline),
    saveView: (input) => saveMutation.mutateAsync(input),
    updateView: (id, patch) => updateMutation.mutateAsync({ id, patch }),
    deleteView: (id) => deleteMutation.mutateAsync(id),
    isSaving: saveMutation.isPending || updateMutation.isPending,
  };
}
